import {
  useState,
  useEffect,
  Reducer,
  useReducer,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { debounce } from "@mui/material";

const defaultInactivityTime = 60e3; // Default is one minute
export const useInactivity = (params?: {
  timeBeforeIdle?: number;
  onIdle?: () => void;
}) => {
  const trackedEvents = ["keydown", "mousemove", "mousedown", "wheel"];
  const { timeBeforeIdle = defaultInactivityTime, onIdle = () => {} } =
    params ?? {};

  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    let _timeout: NodeJS.Timeout;

    const onEvent = () => {
      setIsIdle(false);
      clearTimeout(_timeout);
      _timeout = setTimeout(() => {
        setIsIdle(true);
        onIdle();
      }, timeBeforeIdle);
    };

    trackedEvents.forEach((eventType) => addEventListener(eventType, onEvent));

    return () => {
      clearTimeout(_timeout);
      trackedEvents.forEach((eventType) =>
        removeEventListener(eventType, onEvent)
      );
    };
  }, []);

  return isIdle;
};

export const usePolling = (fn: Function, timing: number = 30e3) => {
  const [intervalHandle, setIntervalHandle] = useState<number | undefined>();

  const start = useCallback(() => {
    fn();
    clearInterval(intervalHandle);
    setIntervalHandle(setInterval(fn, timing));
  }, [intervalHandle, fn]);

  const stop = useCallback(() => {
    clearInterval(intervalHandle);
    setIntervalHandle(undefined);
  }, [intervalHandle]);

  const isPolling = intervalHandle !== undefined;

  const toggle = () => {
    if (isPolling) stop();
    else start();
  };
  useEffect(() => {
    return () => clearInterval(intervalHandle);
  }, [intervalHandle]);

  return { toggle, isPolling, start, stop };
};

export const useSimpleReducer = <T,>(initialState: T) =>
  useReducer(
    (state: T, changes: "reset" | Partial<T>): T =>
      changes === "reset" ? initialState : { ...state, ...changes },
    initialState
  );

export const useDebounce = (callback: () => void) => {
  const ref = useRef(() => {});

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  const debouncedCallback = useMemo(() => {
    const fn = () => {
      ref.current?.();
    };

    return debounce(fn, 1000);
  }, []);

  return debouncedCallback;
};

export const useDebouncedState = <T,>(
  value: T,
  callback: (value: T) => void
): [value: T, setValue: (newValue: T) => void] => {
  const [innerValue, setInnerValue] = useState<T>(value);
  const debounced = useDebounce(() => callback(innerValue));
  useEffect(() => {
    if (value !== innerValue) setInnerValue(value);
  }, [value]);
  const handleSetValue = (newValue: T) => {
    setInnerValue(newValue);
    debounced();
  };
  return [innerValue, handleSetValue];
};
